import { useWorkspaceAsset } from '@gripp/shared-logic';
import type { ColumnData, SlideLayoutLocationState } from '@gripp/shared-ui';
import {
  SlideAction,
  SlideLayout,
  useSlideLayout,
  ViewMode,
} from '@gripp/shared-ui';
import { useMemo, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { AssetRouteProvider } from '../assetRouteContext';
import { AddAssetMediaType } from './addFilesLinksButton';
import { AssetCard } from './assetCard';
import { AssetFileCreateForm } from './assetFileCreateForm';
import { AssetFilters } from './assetFilters';
import EditCategory from './editCategory';

type CategoryItem = { id: string; name: string };

type AssetFileCreateFormPanelProps = {
  type: AddAssetMediaType;
  assetId: string;
  allFileIds: string[];
  selectedCategories: CategoryItem[];
  onCategoriesChange: (categories: CategoryItem[]) => void;
  onCategoryEdit: () => void;
  onComplete: () => void;
};

const AssetFileCreateFormPanel = ({
  type,
  assetId,
  allFileIds,
  selectedCategories,
  onCategoriesChange,
  onCategoryEdit,
  onComplete,
}: AssetFileCreateFormPanelProps) => {
  const { prepareSlideTransition } = useSlideLayout();

  return (
    <AssetFileCreateForm
      type={type}
      assetId={assetId}
      allFileIds={allFileIds}
      onComplete={() => {
        prepareSlideTransition(SlideAction.PREV);
        onComplete();
      }}
      onCategoryEdit={onCategoryEdit}
      selectedCategories={selectedCategories}
      onCategoriesChange={onCategoriesChange}
    />
  );
};

/**
 * SlideLayout panel for adding a file or link to an asset. Mirrors
 * `AssetFileEdit` — the panelized replacement for the old `CreateFileForm`
 * Modal. The `?type=file|link` query param selects which form to render.
 */
export const AssetFileCreate = () => {
  const { assetId = '', issueId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const type: AddAssetMediaType =
    searchParams.get('type') === 'link' ? 'link' : 'file';

  const locationState = location.state as SlideLayoutLocationState | null;
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.NEXT ||
    locationState?.slideLayoutInitialTransition === SlideAction.PREV
      ? locationState.slideLayoutInitialTransition
      : undefined;

  const { workspaceAsset } = useWorkspaceAsset(assetId);
  const allFileIds = useMemo(
    () => (workspaceAsset?.asset?.media ?? []).map((m) => m.id),
    [workspaceAsset?.asset?.media]
  );

  const [selectedCategories, setSelectedCategories] = useState<CategoryItem[]>(
    []
  );
  const [isCategoryPanelOpen, setIsCategoryPanelOpen] = useState(false);

  if (!assetId) return null;

  const basePath = issueId
    ? `/activities/${issueId}/asset/${assetId}`
    : `/assets/${assetId}`;

  const backToFiles = () =>
    navigate(`${basePath}/files/all`, {
      state: { slideLayoutInitialTransition: SlideAction.PREV },
    });

  const leftColumn: ColumnData = {
    title: 'assets.menu.title',
    mainComponent: (
      <AssetRouteProvider
        value={{
          basePath,
          listPath: issueId ? '/activities' : '/assets',
          isWorkspaceMode: false,
          issueId,
        }}
      >
        <AssetCard assetId={assetId} />
      </AssetRouteProvider>
    ),
    headerComponent: <AssetFilters assetId={assetId} />,
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent',
    backButtonTransitionExitColumn: 'both',
    onBackButtonPress: backToFiles,
    disableScroll: false,
  };

  const rightColumn: ColumnData = {
    title: type === 'link' ? 'file.create.linkLabel' : 'file.create.label',
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent',
    onBackButtonPress: backToFiles,
    disableScroll: true,
    mainComponent: (
      <AssetFileCreateFormPanel
        type={type}
        assetId={assetId}
        allFileIds={allFileIds}
        onComplete={backToFiles}
        onCategoryEdit={() => setIsCategoryPanelOpen((prev) => !prev)}
        selectedCategories={selectedCategories}
        onCategoriesChange={setSelectedCategories}
      />
    ),
  };

  const thirdColumn: ColumnData | undefined = isCategoryPanelOpen
    ? {
        title: 'file.create.category',
        showBackButton: true,
        disableScroll: true,
        onBackButtonPress: () => setIsCategoryPanelOpen(false),
        mainComponent: (
          <EditCategory
            selectedCategories={selectedCategories}
            onCategoriesChange={setSelectedCategories}
          />
        ),
      }
    : undefined;

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      thirdColumn={thirdColumn}
      viewMode={ViewMode.DUAL}
      initialTransition={initialTransition}
    />
  );
};
